import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import CustomDatePicker from "../customDatePicker/customDatePicker.component";

export type ContractEndDateDuration = "1m" | "6m" | "1y";

const DURATION_MONTHS: Record<ContractEndDateDuration, number> = {
  "1m": 1,
  "6m": 6,
  "1y": 12,
};

export function calculateContractEndDate(duration: ContractEndDateDuration): Dayjs {
  return dayjs().add(DURATION_MONTHS[duration], "month").endOf("month").startOf("day");
}

interface ContractEndDatePickerProps {
  /** `null`/`undefined` means no end date chosen (yet, or at all). */
  value: Dayjs | null | undefined;
  onChange: (value: Dayjs | null) => void;
  label?: string;
  required?: boolean;
  size?: "small" | "medium";
  sx?: object;
  /** Whether the contract keeps running instead of closing once/if `value` is
   * reached - a separate choice from the date itself, toggled independently
   * via the "Indefinitely" button below. Picking a duration doesn't affect
   * it, and toggling it doesn't touch the date: a contract can have both an
   * end date *and* this set (it just keeps going after that date), or this
   * set with no end date at all (indefinite from the start). */
  indefiniteAfterEndDate: boolean;
  onIndefiniteAfterEndDateChange: (checked: boolean) => void;
}

export default function ContractEndDatePicker({
  value,
  onChange,
  label,
  required,
  size = "small",
  sx,
  indefiniteAfterEndDate,
  onIndefiniteAfterEndDateChange,
}: ContractEndDatePickerProps) {
  const { t } = useTranslation();

  const durationOptions: { value: ContractEndDateDuration; label: string }[] = [
    { value: "1m", label: t("1 month") },
    { value: "6m", label: t("6 months") },
    { value: "1y", label: t("1 year") },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, ...sx }}>
      <CustomDatePicker
        label={label ?? t("End date")}
        value={value ?? null}
        onChange={onChange}
        slotProps={{
          textField: {
            size,
            fullWidth: true,
            required,
          },
        }}
      />
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {durationOptions.map((option) => (
          <Button
            key={option.value}
            size="small"
            variant="outlined"
            onClick={() => onChange(calculateContractEndDate(option.value))}
          >
            {option.label}
          </Button>
        ))}
        <Button
          size="small"
          variant={indefiniteAfterEndDate ? "contained" : "outlined"}
          onClick={() => onIndefiniteAfterEndDateChange(!indefiniteAfterEndDate)}
        >
          {t("Indefinitely")}
        </Button>
      </Stack>
    </Box>
  );
}
